import { useDispatch, useSelector } from "react-redux";
import { collator, ExecutiveDashboardUrl, FactoryPathUrl } from "../../utils/common";
import type { AppDispatch, RootState } from "../../store";
import { Select, Space } from "antd";
import { EnvironmentOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { clearUserInfo, clearWeatherInfo, setSelectedCustomerName, setSelectedSite, setSelectedTimezone } from "../../redux/slices/customerSlice";
import { clearGraphData, clearPowerState, clearGenChangeData, setSelectedGeneratorId, setSelectedSourceId } from "../../redux/slices/powerSlice";
import { clearDashboardData, clearGeneralData, clearPowerFuelData } from "../../redux/slices/dashboardSlice";
import { clearChargerData } from "../../redux/slices/chargerSlice";
import { fetchAllCustomerSites } from "../../redux/services/customerService";
import { useNavigate } from "react-router-dom";
import { DASHBOARD, FUEL, GENERAL, POWER_GENERATION } from "../../utils/constants";
import { UserLogo } from "./Assets";
import { useEffect, useState } from "react";

const SelectDropdownCustSiteMobile = ({ isMobile, lastSegment, onClose, isNewIndustry }: { isMobile: boolean, lastSegment: string, onClose?: () => void, isNewIndustry: boolean }) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { permittedCustomers, permittedCustomersLoading, customerSites, customerSitesLoading, selectedCustomerName, selectedSite } = useSelector((state: RootState) => state.userInfo);
    const { FactoryPath, ExecutivePath } = useSelector((state: RootState) => state.userInfo);
    const { selectedGeneratorId } = useSelector((state: RootState) => state.power);
    const { loadingGenDetailsForSite: isLoadingGeneratorList, generatorList: generatorListFromAPI } = useSelector((state: RootState) => state.dashboard);
    const isDisabled = permittedCustomers.length === 1 || permittedCustomersLoading || (customerSites.length === 0 && customerSitesLoading);
    const [generatorList, setGeneratorList] = useState<any[]>([]);

    useEffect(() => {
        if (generatorListFromAPI) {
            setGeneratorList(
                generatorListFromAPI
                    .filter(
                        (info) =>
                            info.equipment_name !== null &&
                            !["EV Charger", "EV_Charger"].includes(info.equipment_name)
                    )
                    .map((info) => ({
                        value: info.equipment_id,
                        label: info.equipment_name,
                        source_id: info.source_id,
                    }))
            );
        }
    }, [generatorListFromAPI]);
    const handleSelectCustomerChange = (value: string) => {
        dispatch(setSelectedCustomerName(value))
        dispatch(setSelectedTimezone(permittedCustomers.find(data => data.customer === value)?.preferences?.timezone || 'US/Pacific'));
        dispatch(clearUserInfo());
        dispatch(clearPowerFuelData());
        dispatch(clearPowerState());
        dispatch(clearWeatherInfo());
        dispatch(clearGraphData());
        dispatch(clearDashboardData());
        dispatch(clearChargerData());
        dispatch(fetchAllCustomerSites(value)).then((response: any) => {
            const sites = response?.payload || [];
            if (sites.length === 1) {
                dispatch(setSelectedSite(sites[0].name));
                navigate(`/${value}/${sites[0].name}/${GENERAL}`);
            } else if (!FactoryPathUrl(FactoryPath)) {
                dispatch(setSelectedSite(''));
                navigate(`/${value}/${DASHBOARD}`);
            }
        });
        onClose?.();
    }

    const handleSelectSiteChange = (value: string) => {
        dispatch(clearChargerData());
        dispatch(clearPowerState());
        dispatch(clearWeatherInfo());
        dispatch(clearWeatherInfo());
        dispatch(clearGraphData());
        dispatch(clearGeneralData());
        dispatch(clearPowerFuelData());
        dispatch(setSelectedSite(value));
        const selectedSiteObj = customerSites.find((data) => data.name === value)
        if (selectedSiteObj) {
            const present_components = selectedSiteObj?.preferences?.present_components;
            if (present_components.includes(lastSegment) && ![POWER_GENERATION, FUEL].includes(lastSegment)) {
                navigate(`/${selectedCustomerName}/${value}/${lastSegment}`);
            }
            else if (present_components.includes(lastSegment) && [POWER_GENERATION, FUEL].includes(lastSegment)) {
                // navigate(`/${selectedCustomerName}/${value}/${GENERAL}`);
            }
            else {
                navigate(`/${selectedCustomerName}/${value}/${GENERAL}`);
            }
        }
        onClose?.();
    }

    const handleEquipmentChange = (e: any) => {
        e && dispatch(clearGenChangeData());
        e && dispatch(setSelectedGeneratorId(e));
        e &&
            dispatch(
                setSelectedSourceId(
                    generatorList.find((item) => item.value === e)?.source_id
                ) || ""
            );
        e &&
            navigate(
                `/${selectedCustomerName}/${selectedSite}/${POWER_GENERATION}/${e}`
            );
        onClose?.();
    };

    return (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'stretch', flexDirection: 'column', gap: '10px', paddingBottom: '10px' }}>
            {!isNewIndustry && <>
                {isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) &&
                    <Select
                        value={selectedCustomerName}
                        loading={permittedCustomersLoading || (customerSites.length === 0 && customerSitesLoading)}
                        showSearch
                        virtual={false}
                        options={permittedCustomers.map(item => ({ label: item.name, value: item.customer })).sort((a, b) => collator.compare(a.label, b.label))}
                        suffixIcon={<img alt="user" src={UserLogo} className='h-18' />}
                        onChange={handleSelectCustomerChange}
                        disabled={isDisabled}
                        className={isDisabled ? 'insideSelectMobile select-disabled' : 'insideSelectMobile'}
                    />
                }
            </>}
            {!isNewIndustry && <>
                {isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) && selectedSite &&
                    <Select
                        disabled={(customerSites.length === 0 && customerSitesLoading) || permittedCustomersLoading}
                        value={selectedSite}
                        showSearch
                        virtual={false}
                        options={customerSites.map(item => ({ label: item.name, value: item.name })).sort((a, b) => collator.compare(a.label, b.label))}
                        className='insideSelectMobile'
                        suffixIcon={<EnvironmentOutlined />}
                        onChange={handleSelectSiteChange}
                    />
                }
            </>}
            {/* Generator/Equipment Selector - Only show on Power page and mobile */}
            {!isNewIndustry && <>
                {isMobile && !(FactoryPathUrl(FactoryPath) || ExecutiveDashboardUrl(ExecutivePath)) && selectedSite && lastSegment === POWER_GENERATION && ((generatorList && generatorList[0]) || isLoadingGeneratorList) &&
                    <Select
                        value={selectedGeneratorId}
                        virtual={false}
                        allowClear={false}
                        options={generatorList}
                        placeholder="Select a Generator"
                        onClear={() => {
                            dispatch(setSelectedGeneratorId(null));
                            dispatch(setSelectedSourceId(""));
                            navigate(
                                `/${selectedCustomerName}/${selectedSite}/${POWER_GENERATION}`
                            );
                            onClose?.();
                        }}
                        loading={isLoadingGeneratorList}
                        onChange={handleEquipmentChange}
                        className='insideSelectMobile'
                        suffixIcon={<ThunderboltOutlined />}
                    />
                }
            </>}
        </div>
    );
}

export default SelectDropdownCustSiteMobile;
